/**
 * Page Header Component - Enhanced with AI Generator
 * 
 * Features:
 * - Navigation back to dashboard
 * - Dynamic title based on edit mode
 * - AI Generate button to auto-generate job postings
 * - AI Generator Modal integration
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import AIGeneratorModal from './AIGeneratorModal';

export const PageHeader = React.memo(({ isEditMode, onNavigateBack, userProfile, onAIGenerated }) => {
  const navigate = useNavigate();
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  const handleBack = () => {
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      navigate('/employer-dashboard');
    }
  };

  /**
   * Handle AI generation complete
   */
  const handleAIGenerated = useCallback((generatedContent) => {
    setIsAIModalOpen(false);
    if (onAIGenerated) {
      onAIGenerated(generatedContent);
    }
  }, [onAIGenerated]);

  return (
    <>
      <div className="mb-8">
        <div className="flex items-center justify-between space-x-4 mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors"
            >
              <Icon name="ArrowLeft" size={20} />
            </button>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                {isEditMode ? 'Edit Job' : 'Post a New Job'}
              </h1>
              <p className="text-text-secondary">
                {isEditMode
                  ? 'Update your job posting details and requirements'
                  : 'Create and publish your job posting to find the perfect Virtual Assistant'}
              </p>
            </div>
          </div>

          {/* AI Generate Button */}
          {!isEditMode && (
            <button
              onClick={() => setIsAIModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-md hover:shadow-lg whitespace-nowrap font-medium"
              title="Generate job posting with AI"
            >
              <span className="text-lg">✨</span>
              <span className="hidden sm:inline">AI Generate</span>
              <span className="sm:hidden">AI</span>
            </button>
          )}
        </div>
      </div>

      {/* AI Generator Modal */}
      <AIGeneratorModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onGenerate={handleAIGenerated}
        userProfile={userProfile}
      />
    </>
  );
});

PageHeader.displayName = 'PageHeader';
